// src/components/PendingAgreementsList.js
import React from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
const toRelativeMedia = (url) => {
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/media/")) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url.replace(/^https?:\/\/[^/]+/, "");
  }
  const trimmed = url.replace(/^\.?\/*/, "");
  return trimmed.startsWith("media/") ? `/${trimmed}` : `/media/${trimmed}`;
};

const normalizeArtwork = (agreement) => agreement?.artwork || {};

const getArtworkImage = (artwork) => {
  if (!artwork) return null;
  const firstImg = Array.isArray(artwork.images)
    ? (artwork.images[0]?.url || artwork.images[0]?.image || artwork.images[0]?.image_url)
    : null;
  const imageUrl = artwork.primary_image_url || artwork.imageUrl || artwork.image_url || firstImg || null;
  return imageUrl ? toRelativeMedia(imageUrl) : null;
};

const getArtworkTitle = (artwork) => artwork?.title || "Untitled";

const getArtistName = (agreement) => {
  const artist = agreement?.artist;
  if (!artist) return "-";
  if (artist.fullName) return artist.fullName;
  const name = [artist.user?.first_name, artist.user?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || artist.name || "-";
};

const getHostName = (agreement) => {
  const host = agreement?.host;
  if (!host) return "-";
  if (host.fullName) return host.fullName;
  const name = [host.user?.first_name, host.user?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || host.name || "-";
};

const getAgreementTerms = (agreement) => {
  if (agreement?.raw) return getAgreementTerms(agreement.raw);
  const terms = agreement?.terms;
  if (terms && typeof terms === "object") return terms;
  if (typeof terms === "string") {
    try {
      const parsed = JSON.parse(terms);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_) {
      return {};
    }
  }
  return {};
};

const getAgreementPrice = (agreement) => {
  const terms = getAgreementTerms(agreement);
  return (
    agreement?.artworkPrice
    ?? agreement?.artwork?.price
    ?? terms.artwork_price
    ?? terms.price
    ?? agreement?.artwork_price
    ?? agreement?.price
    ?? agreement?.artwork?.price
    ?? null
  );
};

const formatPercent = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  const normalized = num <= 1 ? num * 100 : num;
  return `${normalized.toFixed(0)}%`;
};

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `$${num.toFixed(2)}`;
};

const formatAgreementUpdatedAt = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

export default function PendingAgreementsList({
  agreements,
  onSelect,
  roleLabel,
  emptyLabel = "No pending host requests right now.",
  renderActions,
}) {
  if (!agreements.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {agreements.map((agreement) => {
        const artwork = normalizeArtwork(agreement);
        const image = getArtworkImage(artwork);
        const counterpartyLabel = roleLabel === "artist" ? "Host" : "Artist";
        const counterpartyName =
          roleLabel === "artist"
            ? getHostName(agreement)
            : getArtistName(agreement);

        const terms = getAgreementTerms(agreement);
        const start = agreement?.startDate || agreement?.start_date || terms?.start_date;
        const end = agreement?.endDate || agreement?.end_date || terms?.end_date;
        const commissionArtist = agreement?.commissionArtist ?? agreement?.commission_artist ?? terms?.commission_artist;
        const commissionHost = agreement?.commissionHost ?? agreement?.commission_host ?? terms?.commission_host;
        const artworkPrice = getAgreementPrice(agreement);
        const actions = renderActions?.(agreement);
        const updatedAtLabel = formatAgreementUpdatedAt(agreement?.updatedAt || agreement?.updated_at);

        const Wrapper = onSelect ? CardActionArea : Box;
        const wrapperProps = onSelect
          ? { onClick: () => onSelect?.(agreement) }
          : { sx: { cursor: "default" } };

        return (
          <Card key={agreement.id}>
            <Wrapper {...wrapperProps}>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" } }}>
                {image && (
                  <CardMedia
                    component="img"
                    image={image}
                    alt={artwork.title || "Artwork"}
                    sx={{ width: { xs: "100%", sm: 180 }, height: 140, objectFit: "cover" }}
                  />
                )}
                <CardContent sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: "wrap" }}>
                    <Typography variant="subtitle1">
                      {getArtworkTitle(artwork)}
                    </Typography>
                    <Chip
                      size="small"
                      label={agreement?.status || "Pending"}
                      color={String(agreement?.status || "").includes("PENDING") ? "warning" : "default"}
                    />
                    <Typography variant="body2" color="text.secondary">
                      at {updatedAtLabel}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {counterpartyLabel}: {counterpartyName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Dates: {start || "-"} → {end || "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Split: Artist {formatPercent(commissionArtist)} / Host {formatPercent(commissionHost)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Price: {formatCurrency(artworkPrice)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Revision {agreement?.revision ?? "-"}
                  </Typography>

                  {Array.isArray(actions) && actions.length > 0 && (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
                      {actions.map((action) => (
                        <Button
                          key={action.key || action.label}
                          size="small"
                          variant={action.variant || "outlined"}
                          color={action.color || "primary"}
                          disabled={Boolean(action.disabled)}
                          onClick={(event) => {
                            event.stopPropagation();
                            action.onClick?.(agreement);
                          }}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Box>
            </Wrapper>
          </Card>
        );
      })}
    </Stack>
  );
}
